import React, { useCallback } from 'react';
import { ShowSection } from 'framework/ui/layout';
import RiskSectionHeader from './RiskSectionHeader';
import { CloseFormShowButton, EditFormShowButton, SaveFormShowButton, useFormShowContext } from 'framework/form';
import EscalationButton from './EscalationButton';
import DeleteRiskModal from '../createupdate/DeleteRiskModal';
import { Box, Stack, Typography } from '@mui/material';
import { Grid2 as Grid } from '@mui/material';
import RiskOwnerField from '../RiskOwnerField';
import { PROJECT_PROBABILITY_OPTIONS, RISK_TYPE_OPTIONS, RisksType, UseRiskByIdResult } from 'domain/shared/newProject';
import RiskForm from '../createupdate/RiskForm';
import useRiskPermissions from '../useRiskPermissions';
import { SanitizedHtml } from 'framework/utils';
import { useShowContext } from 'framework/show';

const MainRiskSection = ({ onEscalationUpdated }: { onEscalationUpdated: UseRiskByIdResult['refetch'] }) => {
  const { data: showData } = useShowContext<RisksType>();
  const risk = showData?.getData();
  const permissions = useRiskPermissions({
    createdBy: risk.createdBy,
    riskOwner: risk.riskOwner,
  });
  const { canEdit, canDelete, canEscalate, canDeescalate } = permissions;
  const canToggleEscalation = risk.isEscalated ? canDeescalate : canEscalate;

  const { isEditMode } = useFormShowContext();
  const renderActions = useCallback(() => {
    return isEditMode ? (
      <>
        <SaveFormShowButton />
        <CloseFormShowButton />
      </>
    ) : (
      <Box display={'flex'} flexDirection={'row'} gap={1}>
        {canDelete && <DeleteRiskModal projectId={String(risk.projectId)} id={risk.id} isInMainSection />}
        {canToggleEscalation && (
          <EscalationButton isEscalated={Boolean(risk.isEscalated)} onEscalationUpdated={onEscalationUpdated} />
        )}
        {canEdit && <EditFormShowButton />}
      </Box>
    );
  }, [
    isEditMode,
    canDelete,
    canToggleEscalation,
    canEdit,
    onEscalationUpdated,
    risk.id,
    risk.projectId,
    risk.isEscalated,
  ]);

  return (
    <ShowSection
      justifyContent="space-between"
      titleId="risk-group-title"
      title={<RiskSectionHeader isEdit={isEditMode} data={risk} actions={renderActions()} />}
    >
      {isEditMode ? (
        <RiskForm isUpdateForm permissions={permissions} />
      ) : (
        <Stack spacing={2}>
          <Grid container spacing={1} size={6}>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Probability:
                </Typography>
              </Grid>
              <Grid size={8}>
                <Typography>{PROJECT_PROBABILITY_OPTIONS[risk.probability ?? '']}</Typography>
              </Grid>
            </Grid>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Type:
                </Typography>
              </Grid>
              <Grid size={8}>
                <Typography>{RISK_TYPE_OPTIONS[risk.type ?? '']}</Typography>
              </Grid>
            </Grid>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Severity:
                </Typography>
              </Grid>
              <Grid size={8}>
                <Typography>{PROJECT_PROBABILITY_OPTIONS[risk.severity ?? '']}</Typography>
              </Grid>
            </Grid>
            <Grid size={6} container alignItems={'center'}>
              <Grid size={4}>
                <Typography color="textSecondary" variant="body2">
                  Owner:
                </Typography>
              </Grid>
              <Grid size={8}>
                <RiskOwnerField source={'riskOwner'} label={''} />
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1} size={12} pb={1}>
            <Grid size={1}>
              <Typography color="textSecondary" variant="body2">
                Description:
              </Typography>
            </Grid>
            <Grid size={11}>
              <SanitizedHtml html={risk.description || 'No description provided'} />
            </Grid>
            <Grid size={1}>
              <Typography color="textSecondary" variant="body2">
                Strategy:
              </Typography>
            </Grid>
            <Grid size={11}>
              <SanitizedHtml html={risk.mitigationStrategy || 'No strategy provided'} />
            </Grid>
          </Grid>
        </Stack>
      )}
    </ShowSection>
  );
};
export default MainRiskSection;
